import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslate } from 'react-admin';

import { Loader } from '@/components/Loader';
import { filtersDataProvider } from '@/dataProvider';
import { ICompaniesFilter, ICompaniesSelect } from '@/types';
import { DEFAULT_NOT_SELECTED_COMPANIES } from '@/constants';

interface ICompaniesSelectProps {
  selectedCompanies?: string[];
  setSelectedCompanies: Dispatch<SetStateAction<string[] | undefined>>;
}

function CompaniesSelect({
  selectedCompanies,
  setSelectedCompanies,
}: ICompaniesSelectProps): JSX.Element {
  const t = useTranslate();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [companiesFilter, setCompaniesFilter] = useState<ICompaniesSelect[]>([]);

  const handleCompanyChange = (event: SelectChangeEvent<string[]>): void => {
    const selected = event.target.value as string[];

    setSelectedCompanies(selected);
  };

  const getCompaniesFilter = async (): Promise<ICompaniesFilter[]> => {
    setIsLoading(true);
    const companies = await filtersDataProvider.getCompanies({
      isActive: true,
    });
    const selectData = companies.map(item => ({ ...item, name: item.company_name }));
    setCompaniesFilter(selectData);
    const notSelectedCompaniesIds = companies.filter(item =>
      DEFAULT_NOT_SELECTED_COMPANIES.includes(item.company_name),
    );
    const selectedCompaniesIds = selectData
      .map(item => item.id)
      .filter(id => !notSelectedCompaniesIds.find(company => company.id === id));

    setSelectedCompanies(selectedCompaniesIds);
    setIsLoading(false);

    return companies;
  };

  useEffect(() => {
    void getCompaniesFilter();
  }, []);

  return (
    <>
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{ height: '90vh', width: '73vw' }}
        >
          <Loader />
        </Box>
      ) : (
        <FormControl fullWidth>
          <InputLabel id="dashboard-company-select-label">
            {t('resources.dashboard.choose_company')}
          </InputLabel>
          <Select
            value={selectedCompanies as any}
            labelId="dashboard-company-select-label"
            id="dashboard-company-select"
            onChange={handleCompanyChange}
            multiple
            renderValue={selected =>
              selected
                .map(item => companiesFilter.find(company => company.id === item)?.name)
                .filter(item => !!item)
                .join(', ')
            }
          >
            {companiesFilter.map(({ name, id }) => {
              return (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      )}
    </>
  );
}

export default CompaniesSelect;
